import { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Fingerprint } from 'lucide-react';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Card, Input, Btn, Alert, Checkbox } from '../components/UI';
import { useAuth } from '../contexts/AuthContext';
import { passkeyApi, passkeyErrorMessage } from '../services/api';
import { useLoginThrottle, TurnstileWidget, ThrottleHint } from '../components/LoginThrottle';

// ⚠ `next` a query-stringből jön, tehát NEM megbízható bemenet — csak
// SAJÁT, relatív útvonalra engedünk átirányítani (nyílt redirect elleni
// védelem). `//evil.com` sémafüggetlen URL-ként MÁS originre vinne, ezt is
// kizárjuk.
function safeNext(next) {
  if (!next || !next.startsWith('/') || next.startsWith('//')) return null;
  return next;
}

export default function Login() {
  const { login, applyLoginResult } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // 2026-08-04 (F-1a): alapból KIKAPCSOLVA. Bejelölés nélkül a munkamenet a
  // böngésző bezárásakor véget ér – korábban minden bejelentkezés automatikusan
  // 30 napig élt, választás nélkül.
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const passkeySupported = browserSupportsWebAuthn();
  // D-10 (2026-09-05): fiókonkénti késleltetés + Turnstile a 10. hiba után.
  // A szerver fejlécei vezérlik (Retry-After, X-Turnstile-Required) — a
  // felület csak visszaszámol és a widgetet rajzolja, ha kell.
  const throttle = useLoginThrottle();

  // Sikeres belépés utáni navigáció. A `/go/{token}` (WP-01) esetén ez
  // KIFEJEZETTEN teljes oldal-újratöltés (`window.location.href`), NEM a
  // React Router `navigate()`-je: a `/go/{token}`-nek a BACKENDET kell újra
  // elérnie a most beállt session-sütivel, amit egy kliens-oldali navigate
  // nem hívna meg (a frontend routerében nincs is `/go/*` route — azt az
  // nginx a backendre viszi).
  const goAfterLogin = () => {
    const next = safeNext(searchParams.get('next'));
    if (next) {
      window.location.href = next;
      return;
    }
    navigate(location.state?.from || '/', { replace: true });
  };
  // 2026-08-24, Gábor döntése: a passkey legyen ELSŐDLEGES - mobilon
  // ujjlenyomat/Face ID legyen az alapértelmezett belépési mód, a jelszó
  // csak tartalék, összecsukva. Ha a böngésző nem támogatja a WebAuthn-t,
  // nincs mit összecsukni - a jelszavas forma az egyetlen út.
  const [showPasswordForm, setShowPasswordForm] = useState(!passkeySupported);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password, remember, throttle.turnstileToken);
      throttle.reset();
      goAfterLogin();
    } catch (err) {
      setError(throttle.absorb(err, 'Hibás e-mail vagy jelszó.'));
    } finally {
      setLoading(false);
    }
  };

  // Passkey belépés (wiki/proposals/deferred-features.md §15) — kiegészítő
  // mód a jelszó mellett, discoverable credential-lel: nem kell előre e-mailt
  // beírni, a platform-authenticator (Windows Hello, Touch ID stb.) adja meg,
  // melyik fiókról van szó.
  const loginWithPasskey = async () => {
    setError(null);
    setPasskeyLoading(true);
    try {
      const { challenge_id: challengeId, options } = await passkeyApi.authenticationOptions();
      const credential = await startAuthentication({ optionsJSON: options });
      const data = await passkeyApi.authenticationVerify(challengeId, credential, remember);
      applyLoginResult(data);
      goAfterLogin();
    } catch (err) {
      // A böngésző saját megszakítás-jelzését (a user "Mégse"-t nyomott a
      // platform-dialógusban) NE mutassuk hibaként.
      if (err?.name !== 'NotAllowedError') {
        setError(passkeyErrorMessage(err, 'A passkey-bejelentkezés sikertelen.'));
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: 'var(--bg-surface)',
    }}>
      <Card title="PhenoFin" subtitle="Jelentkezz be a folytatáshoz" style={{ width: 340 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          {error && <Alert type="danger">{error}</Alert>}

          <div style={{ marginTop: 2, marginBottom: passkeySupported && !showPasswordForm ? 0 : 2 }}>
            <Checkbox label="Maradjak bejelentkezve ezen a gépen"
              checked={remember} onChange={e => setRemember(e.target.checked)} />
            <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)',
              margin: '0.4rem 0 0 23px', lineHeight: 1.45 }}>
              {remember
                ? 'A böngésző 30 napig bejelentkezve tart – a gép és a böngésző újraindítása után is. Csak saját, jelszóval védett gépen használd: aki hozzáfér a géphez, jelszó nélkül belép a rendszerbe.'
                : 'A böngésző bezárásakor kiléptet a rendszer, és újra kell jelentkezned. Ez a biztonságosabb, közös vagy idegen gépen ez az ajánlott.'}
            </p>
          </div>

          {/* Passkey ELSŐDLEGES belépési mód (2026-08-24, Gábor döntése): nagy,
              feltűnő gomb, discoverable credential - nem kell előre e-mailt beírni. */}
          {passkeySupported && (
            <Btn type="button" loading={passkeyLoading} onClick={loginWithPasskey}
              style={{ justifyContent: 'center', padding: '0.7rem 0.9rem', fontSize: '0.92rem' }}>
              <Fingerprint size={20} style={{ marginRight: 8 }} /> Belépés ujjlenyomattal / Face ID-vel
            </Btn>
          )}

          {passkeySupported && !showPasswordForm && (
            <button type="button" onClick={() => setShowPasswordForm(true)}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)',
                fontSize: '0.76rem', textAlign: 'center', cursor: 'pointer', padding: '2px 0' }}>
              Inkább jelszóval jelentkeznék be
            </button>
          )}

          {showPasswordForm && (
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {passkeySupported && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '2px 0' }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>vagy</span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>
              )}
              <Input label="E-mail" type="email" value={email} autoFocus={!passkeySupported}
                onChange={e => setEmail(e.target.value)} required />
              <Input label="Jelszó" type="password" value={password}
                onChange={e => setPassword(e.target.value)} required />
              {throttle.turnstile && (
                <TurnstileWidget siteKey={throttle.turnstile.siteKey} onToken={throttle.onToken} />
              )}
              <ThrottleHint throttle={throttle} />
              <Btn type="submit" variant={passkeySupported ? 'ghost' : 'primary'} loading={loading}
                disabled={throttle.blocked} style={{ justifyContent: 'center' }}>
                Bejelentkezés jelszóval
              </Btn>
            </form>
          )}

          <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center',
            margin: 0, lineHeight: 1.45 }}>
            Minden utalást külön, a passkeyeddel hagysz jóvá.
          </p>
        </div>
      </Card>
    </div>
  );
}
